import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  AttachmentBlock,
  ChatItem,
  UserMessageItem,
} from '../../contracts/harness.types';

// The stable item id for the single optimistic first-turn bubble. Constant (not
// project-scoped) so the bubble is the SAME element across the provisional->real
// hand-off — a mount-time entrance animation can't replay on a node that never
// remounts. Unique because it's injected only while the real timeline is empty,
// and it is never a server-generated id.
export const OPTIMISTIC_FIRST_TURN_ITEM_ID = 'pending-first-turn';

interface StoredPreview {
  text: string;
  animated: boolean;
  attachments: AttachmentBlock[];
}

// Cross-mount survival for the optimistic first-turn bubble, keyed by project.
// A canvas-host remount (routed flow) or the provisional->real hand-off restores
// the bubble on the FIRST render of the new mount instead of blinking out.
// Stored entries are always `animated: true`: a restore means a prior mount
// already played the entrance, so the restored bubble renders static. The only
// writers are `showPreview` and `seedOptimisticFirstTurn` — the declarative
// `previewText` prop never writes here, so a preview shown on one surface can't
// leak into another surface's view of the same project via the store.
const storedPreviewByProjectId = new Map<string, StoredPreview>();

const EMPTY_PREVIEW_ATTACHMENTS: AttachmentBlock[] = [];

/**
 * Record an already-shown optimistic first-turn bubble for a project so the next
 * render that resolves this `projectId` paints it immediately and static (no
 * entrance replay). The in-session Work panel calls this synchronously when the
 * create resolves — before flipping `ProjectChat` to the real id — to hold the
 * bubble across the window between the id landing and the seeded turn calling
 * `showPreview`. No-op for an empty prompt.
 */
export const seedOptimisticFirstTurn = (
  projectId: string,
  text: string | null,
  attachments: readonly AttachmentBlock[] = []
): void => {
  const trimmed = text?.trim() ?? '';
  if (trimmed || attachments.length > 0) {
    storedPreviewByProjectId.set(projectId, {
      text: trimmed,
      animated: true,
      attachments: [...attachments],
    });
  }
};

/** Test-only: clear the cross-mount store between Jest cases. */
export const __resetOptimisticFirstTurnForTests = (): void => {
  storedPreviewByProjectId.clear();
};

interface UseOptimisticFirstTurnArgs {
  projectId: string;
  items: ChatItem[];
  runFailed: boolean;
  runClosed: boolean;
  /**
   * Declarative preview text (the RPA overlay's `pendingUserMessage`, or
   * provisional `ProjectChat`'s `provisionalFirstTurnText`). Render-only — it
   * never writes the cross-mount store.
   */
  previewText?: string | null;
  /**
   * True when the declarative preview was already shown (and animated) on
   * another surface — e.g. the RPA overlay's raw bubble — so this one renders
   * static instead of replaying the entrance.
   */
  previewAlreadyAnimated?: boolean;
  /**
   * Bumps when a turn is cancelled before any output. The transport drops that
   * turn, so a first turn leaves the timeline empty again — the preview must go
   * with it, or the bubble and its thinking loader stay up with nothing running.
   */
  discardedTurnNonce?: number;
}

interface UseOptimisticFirstTurnResult {
  displayItems: ChatItem[];
  shouldAnimateUserMessage: (item: UserMessageItem) => boolean;
  /**
   * Show the optimistic bubble for an in-flight seeded turn (routed flow).
   * Animates once on this mount, and survives a later remount via the store.
   */
  showPreview: (text: string, attachments?: readonly AttachmentBlock[]) => void;
  /** Drop the optimistic bubble (seeded submit rejected). */
  clearPreview: () => void;
}

/**
 * The single representation of the optimistic first-turn bubble — the local
 * user-message bubble shown while the opening turn's POST is in flight. Turn 1
 * crosses create → navigate → remount with no `agentRunId` yet, so there is no
 * `useRunStreamLifecycle` instance to hold the optimistic state; this hook is
 * what carries the bubble across that gap.
 *
 * One module store + one render-derived preview replace the previous pair of
 * text-matching machines (the timeline's `previewUserMessage` path and the
 * seeded-preview hook) plus their module-global stand-in bridge. Three producers
 * feed it — a declarative `previewText` prop (RPA overlay + provisional chat),
 * the imperative `showPreview` (routed seeded turn), and the module
 * `seedOptimisticFirstTurn` (the future in-session panel hand-off) — but the
 * bubble has a single stable id, so it never replays its entrance across a
 * path/id swap, and a single `animated` flag (not text equality) decides whether
 * the entrance plays.
 */
export const useOptimisticFirstTurn = ({
  projectId,
  items,
  runFailed,
  runClosed,
  previewText,
  previewAlreadyAnimated = false,
  discardedTurnNonce = 0,
}: UseOptimisticFirstTurnArgs): UseOptimisticFirstTurnResult => {
  // Imperative preview (routed flow). Local so it animates once on this mount;
  // also mirrored into the store (`animated: true`) for remount survival.
  const [localPreview, setLocalPreview] = useState<StoredPreview | null>(null);
  // Persisted id of the real user message that replaced a preview we showed, so
  // its entrance stays suppressed after the source clears (e.g. a canvas-host
  // remount deletes the store entry). Set by the effect below, on the frame the
  // preview source is still live.
  const [suppressedRealId, setSuppressedRealId] = useState<string | null>(null);

  // Reset per-project state the moment `projectId` changes — render-phase (the
  // documented "adjust state while rendering" pattern), NOT an effect, so a
  // navigation A->B never commits even one frame of A's preview in B's empty
  // timeline.
  const [prevProjectId, setPrevProjectId] = useState(projectId);
  // The project whose preview a discarded turn retired. Holds against every
  // source, including a prop the embedder has not cleared, until a new preview
  // arrives or the project changes.
  const [discardedProjectId, setDiscardedProjectId] = useState<string | null>(null);
  if (prevProjectId !== projectId) {
    setPrevProjectId(projectId);
    setLocalPreview(null);
    setSuppressedRealId(null);
    setDiscardedProjectId(null);
  }

  const [prevDiscardedTurnNonce, setPrevDiscardedTurnNonce] =
    useState(discardedTurnNonce);
  if (prevDiscardedTurnNonce !== discardedTurnNonce) {
    setPrevDiscardedTurnNonce(discardedTurnNonce);
    setLocalPreview(null);
    setDiscardedProjectId(projectId);
  }
  const propText = previewText?.trim() ? previewText.trim() : null;
  const [prevPropText, setPrevPropText] = useState(propText);
  if (prevPropText !== propText) {
    setPrevPropText(propText);
    if (propText) setDiscardedProjectId(null);
  }
  const previewDiscarded = discardedProjectId === projectId;

  const stored = storedPreviewByProjectId.get(projectId) ?? null;
  // Resolution order: declarative prop -> imperative local -> cross-mount store.
  const source: StoredPreview | null = useMemo(
    () =>
      propText
        ? {
            text: propText,
            animated: previewAlreadyAnimated,
            attachments: EMPTY_PREVIEW_ATTACHMENTS,
          }
        : (localPreview ?? stored),
    [propText, previewAlreadyAnimated, localPreview, stored]
  );
  const effectiveText = source?.text ?? null;
  const previewAttachments = useMemo(
    () => source?.attachments ?? EMPTY_PREVIEW_ATTACHMENTS,
    [source]
  );
  const animated = source?.animated ?? false;

  const isPreviewing =
    (!!effectiveText || previewAttachments.length > 0) &&
    items.length === 0 &&
    !previewDiscarded &&
    !runFailed &&
    !runClosed;

  useEffect(() => {
    if (previewDiscarded) storedPreviewByProjectId.delete(projectId);
  }, [previewDiscarded, projectId]);

  // Once the real timeline has content (or the run terminated), drop the store
  // entry; and if a preview was still in play on this frame (`effectiveText`),
  // lock the entrance of the real message that replaced it — so it stays static
  // even after the source clears. The `effectiveText` guard is what distinguishes
  // a preview replacement from a plain history load (which must animate normally).
  useEffect(() => {
    if (items.length === 0 && !runFailed && !runClosed) return;
    storedPreviewByProjectId.delete(projectId);
    if (!effectiveText && previewAttachments.length === 0) return;
    setLocalPreview(null);
    const replacementId = items.find((item) => item.kind === 'user_message')?.id;
    if (replacementId) setSuppressedRealId(replacementId);
  }, [effectiveText, items, previewAttachments.length, projectId, runClosed, runFailed]);

  const displayItems = useMemo<ChatItem[]>(() => {
    if (!isPreviewing) return items;
    return [
      {
        kind: 'user_message',
        id: OPTIMISTIC_FIRST_TURN_ITEM_ID,
        text: effectiveText ?? '',
        attachments: previewAttachments,
      },
    ];
  }, [effectiveText, isPreviewing, items, previewAttachments]);

  const firstRealUserMessageId = useMemo(
    () => items.find((item) => item.kind === 'user_message')?.id,
    [items]
  );

  const shouldAnimateUserMessage = useCallback(
    (item: UserMessageItem) => {
      // The optimistic bubble animates only on its first appearance.
      if (item.id === OPTIMISTIC_FIRST_TURN_ITEM_ID) return !animated;
      // The first real user message, while a preview is still in play, is the
      // one that replaced the optimistic bubble — suppress its entrance
      // render-phase (the source is always still live on the replacement frame),
      // then `suppressedRealId` holds it static after the source clears.
      if (item.id === suppressedRealId) return false;
      if (
        item.id === firstRealUserMessageId &&
        (!!effectiveText || previewAttachments.length > 0)
      ) {
        return false;
      }
      return true;
    },
    [
      effectiveText,
      previewAttachments.length,
      suppressedRealId,
      animated,
      firstRealUserMessageId,
    ]
  );

  const showPreview = useCallback(
    (text: string, attachments: readonly AttachmentBlock[] = []) => {
      const trimmed = text.trim();
      if (!trimmed && attachments.length === 0) return;
      const alreadyShown = storedPreviewByProjectId.has(projectId);
      const preview: StoredPreview = {
        text: trimmed,
        animated: alreadyShown,
        attachments: [...attachments],
      };
      setLocalPreview(preview);
      setDiscardedProjectId(null);
      storedPreviewByProjectId.set(projectId, { ...preview, animated: true });
    },
    [projectId]
  );

  const clearPreview = useCallback(() => {
    setLocalPreview(null);
    storedPreviewByProjectId.delete(projectId);
    setSuppressedRealId(null);
  }, [projectId]);

  return { displayItems, shouldAnimateUserMessage, showPreview, clearPreview };
};
